import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { useLocation } from "wouter-preact"
import { Button } from "@/components/ui/button"
import { api_fetch_throws } from "@/api/client"
import { WalmartBotStatus, bot_token_message } from "@/api/walmart"

interface WalmartBotTokenSettingsProps {
  integrationId: string
  className?: string
}

const SEVERITY_CLASS = {
  ok: "text-gray-600",
  warn: "text-amber-600",
  error: "text-red-600",
} as const

function StatusLine({ status }: { status: WalmartBotStatus }) {
  const message = bot_token_message(status)
  return (
    <div class="space-y-1">
      <p class={`text-sm font-medium ${SEVERITY_CLASS[message.severity]}`}>{message.text}</p>
      {status.bot_token_expires_at && (
        <p class="text-xs text-gray-400">
          Expires {format(parseISO(status.bot_token_expires_at), "MMM d, yyyy")}
        </p>
      )}
    </div>
  )
}

/**
 * Bot token health for a Walmart connection.
 *
 * Walmart expires bot tokens every 60 days, and an expired token silently stops the sync — so this
 * exists to make the rotation visible before the data goes stale rather than after.
 */
export function WalmartBotTokenSettings({ integrationId, className }: WalmartBotTokenSettingsProps) {
  const [, setLocation] = useLocation()
  const { data: status, error } = useSWR<WalmartBotStatus>(`/integration/${integrationId}/walmart/status`, (url: string) =>
    api_fetch_throws<WalmartBotStatus>(url)
  )

  if (error) {
    return <p class={`text-sm text-gray-500 ${className ?? ""}`}>Could not load bot token status.</p>
  }
  if (!status) {
    return <p class={`text-sm text-gray-500 ${className ?? ""}`}>Loading…</p>
  }

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <StatusLine status={status} />

      {status.last_login_error && (
        <div class="rounded-lg bg-red-50 p-3">
          <p class="text-sm text-red-700">{status.last_login_error}</p>
        </div>
      )}

      <p class="text-sm text-gray-500">
        Generate a new token in Retail Link under User Management → your bot user → View Profile → Generate Bot Token,
        then save it here.
      </p>

      <Button variant="outline" onClick={() => setLocation("/settings/connect/walmart")}>
        Update bot token
      </Button>

      {status.last_synced_at && (
        <p class="text-xs text-gray-400">
          Last Retail Link sync {format(parseISO(status.last_synced_at), "MMM d, yyyy")}
        </p>
      )}
    </div>
  )
}
